import type { FamilyTheme } from "@family/shared";

export interface Palette {
  name: string;
  description: string;
  light: Record<string, string>;
  dark: Record<string, string>;
}

/*
 * Per-family palettes. Keys are stored in the families table, so they must
 * stay stable ("classic", "editorial", …) even though display names evolve.
 *
 * "classic" mirrors the :root defaults in index.css — keep the two in sync.
 * Every palette defines the full accent token set (--c-accent-*) because
 * buttons and links derive text/tint colors from them; a palette that only
 * set --c-accent would inherit mismatched hover/contrast values.
 */

const SERIF = '"Iowan Old Style", Georgia, "Times New Roman", serif';

export const PALETTES: Record<FamilyTheme, Palette> = {
  classic: {
    name: "Heritage",
    description: "Warm cream with oxblood accents — archival and warm (default)",
    light: {
      "--c-accent": "#7c2f2a",
      "--c-accent-hover": "#632421",
      "--c-accent-active": "#4e1b19",
      "--c-accent-contrast": "#ffffff",
      "--c-accent-tint": "rgba(124, 47, 42, 0.08)",
      "--c-text": "#2b2118",
      "--c-text-muted": "#5c5245",
      "--c-text-subtle": "#857a68",
      "--c-bg": "#faf7f1",
      "--c-bg-alt": "#f1ebdf",
      "--c-bg-subtle": "#fdfbf6",
      "--c-border": "#e2dac9",
      "--c-border-strong": "#a2937a",
      "--font-heading": SERIF,
    },
    dark: {
      "--c-accent": "#d29a6b",
      "--c-accent-hover": "#ddab82",
      "--c-accent-active": "#c2854f",
      "--c-accent-contrast": "#241a12",
      "--c-accent-tint": "rgba(210, 154, 107, 0.12)",
      "--c-text": "#ece4d4",
      "--c-text-muted": "#b5a992",
      "--c-text-subtle": "#8a7d66",
      "--c-bg": "#181410",
      "--c-bg-alt": "#241e17",
      "--c-bg-subtle": "#1d1813",
      "--c-border": "#3a3226",
      "--c-border-strong": "#60533f",
      "--font-heading": SERIF,
    },
  },
  editorial: {
    name: "Ink",
    description: "Soft parchment with deep navy — bookish and calm",
    light: {
      "--c-accent": "#31456e",
      "--c-accent-hover": "#263756",
      "--c-accent-active": "#1d2a42",
      "--c-accent-contrast": "#ffffff",
      "--c-accent-tint": "rgba(49, 69, 110, 0.08)",
      "--c-text": "#1f2430",
      "--c-text-muted": "#4c5261",
      "--c-text-subtle": "#767d8c",
      "--c-bg": "#f8f7f4",
      "--c-bg-alt": "#efede7",
      "--c-bg-subtle": "#fbfaf8",
      "--c-border": "#dfdcd2",
      "--c-border-strong": "#9a958a",
      "--font-heading": SERIF,
    },
    dark: {
      "--c-accent": "#8fa8d9",
      "--c-accent-hover": "#a6bbe4",
      "--c-accent-active": "#7791c7",
      "--c-accent-contrast": "#131722",
      "--c-accent-tint": "rgba(143, 168, 217, 0.12)",
      "--c-text": "#e4e6ec",
      "--c-text-muted": "#a6abb8",
      "--c-text-subtle": "#767d8c",
      "--c-bg": "#14161c",
      "--c-bg-alt": "#1e2129",
      "--c-bg-subtle": "#191c23",
      "--c-border": "#2e323d",
      "--c-border-strong": "#4c5261",
      "--font-heading": SERIF,
    },
  },
  modern: {
    name: "Modern",
    description: "Clean neutrals with indigo — minimal and contemporary",
    light: {
      "--c-accent": "#4f46e5",
      "--c-accent-hover": "#4338ca",
      "--c-accent-active": "#3730a3",
      "--c-accent-contrast": "#ffffff",
      "--c-accent-tint": "rgba(79, 70, 229, 0.08)",
      "--c-text": "#1e1e24",
      "--c-text-muted": "#55555e",
      "--c-text-subtle": "#75757e",
      "--c-bg": "#fafafa",
      "--c-bg-alt": "#f1f1f3",
      "--c-bg-subtle": "#ffffff",
      "--c-border": "#e5e5ea",
      "--c-border-strong": "#a0a0aa",
      "--font-heading": "var(--font-sans)",
    },
    dark: {
      "--c-accent": "#8b85f2",
      "--c-accent-hover": "#a5a0f6",
      "--c-accent-active": "#6f68e8",
      "--c-accent-contrast": "#17171b",
      "--c-accent-tint": "rgba(139, 133, 242, 0.14)",
      "--c-text": "#e6e6ea",
      "--c-text-muted": "#a4a4ae",
      "--c-text-subtle": "#74747e",
      "--c-bg": "#17171b",
      "--c-bg-alt": "#222228",
      "--c-bg-subtle": "#1c1c21",
      "--c-border": "#33333c",
      "--c-border-strong": "#52525e",
      "--font-heading": "var(--font-sans)",
    },
  },
  "dark-gold": {
    name: "Regal",
    description: "Ivory and charcoal with gold — rich and celebratory",
    light: {
      "--c-accent": "#8a6a12",
      "--c-accent-hover": "#6f550e",
      "--c-accent-active": "#574209",
      "--c-accent-contrast": "#ffffff",
      "--c-accent-tint": "rgba(138, 106, 18, 0.1)",
      "--c-text": "#241f14",
      "--c-text-muted": "#55503f",
      "--c-text-subtle": "#7d7660",
      "--c-bg": "#faf8f2",
      "--c-bg-alt": "#f1eee2",
      "--c-bg-subtle": "#fdfcf8",
      "--c-border": "#e4ddc8",
      "--c-border-strong": "#a89a76",
      "--font-heading": SERIF,
    },
    dark: {
      "--c-accent": "#d4ab4e",
      "--c-accent-hover": "#e0bd6c",
      "--c-accent-active": "#c19638",
      "--c-accent-contrast": "#201a10",
      "--c-accent-tint": "rgba(212, 171, 78, 0.14)",
      "--c-text": "#ede6d3",
      "--c-text-muted": "#beb298",
      "--c-text-subtle": "#8c8168",
      "--c-bg": "#171410",
      "--c-bg-alt": "#221e16",
      "--c-bg-subtle": "#1c1812",
      "--c-border": "#3a3424",
      "--c-border-strong": "#62573c",
      "--font-heading": SERIF,
    },
  },
  ocean: {
    name: "Ocean",
    description: "Deep teal on sea-mist neutrals — calm and fresh",
    light: {
      "--c-accent": "#0d6e63",
      "--c-accent-hover": "#0a574e",
      "--c-accent-active": "#07423c",
      "--c-accent-contrast": "#ffffff",
      "--c-accent-tint": "rgba(13, 110, 99, 0.08)",
      "--c-text": "#16262c",
      "--c-text-muted": "#4b5c61",
      "--c-text-subtle": "#74868b",
      "--c-bg": "#f7fafa",
      "--c-bg-alt": "#eaf1f0",
      "--c-bg-subtle": "#fcfefe",
      "--c-border": "#d9e4e2",
      "--c-border-strong": "#8fa5a2",
      "--font-heading": "var(--font-sans)",
    },
    dark: {
      "--c-accent": "#55c2b2",
      "--c-accent-hover": "#74d1c3",
      "--c-accent-active": "#3daa99",
      "--c-accent-contrast": "#0b1b18",
      "--c-accent-tint": "rgba(85, 194, 178, 0.13)",
      "--c-text": "#dfeae8",
      "--c-text-muted": "#9db3af",
      "--c-text-subtle": "#6d8480",
      "--c-bg": "#0f1615",
      "--c-bg-alt": "#18221f",
      "--c-bg-subtle": "#131c1a",
      "--c-border": "#263330",
      "--c-border-strong": "#42544f",
      "--font-heading": "var(--font-sans)",
    },
  },
  clay: {
    name: "Clay",
    description: "Bright terracotta on sand — friendly and warm",
    light: {
      "--c-accent": "#bc5233",
      "--c-accent-hover": "#a04429",
      "--c-accent-active": "#833720",
      "--c-accent-contrast": "#ffffff",
      "--c-accent-tint": "rgba(188, 82, 51, 0.08)",
      "--c-text": "#2a201b",
      "--c-text-muted": "#5e5148",
      "--c-text-subtle": "#87786d",
      "--c-bg": "#faf7f4",
      "--c-bg-alt": "#f2ece6",
      "--c-bg-subtle": "#fdfbf9",
      "--c-border": "#e7ddd3",
      "--c-border-strong": "#ab9985",
      "--font-heading": "var(--font-sans)",
    },
    dark: {
      "--c-accent": "#e39877",
      "--c-accent-hover": "#ecac90",
      "--c-accent-active": "#d68159",
      "--c-accent-contrast": "#24140d",
      "--c-accent-tint": "rgba(227, 152, 119, 0.13)",
      "--c-text": "#ede4dd",
      "--c-text-muted": "#b5a79b",
      "--c-text-subtle": "#8a7c70",
      "--c-bg": "#171310",
      "--c-bg-alt": "#231d18",
      "--c-bg-subtle": "#1c1713",
      "--c-border": "#392f27",
      "--c-border-strong": "#5f5041",
      "--font-heading": "var(--font-sans)",
    },
  },
  forest: {
    name: "Forest",
    description: "Sage and deep green — natural and grounded",
    light: {
      "--c-accent": "#2f5d24",
      "--c-accent-hover": "#254a1c",
      "--c-accent-active": "#1c3915",
      "--c-accent-contrast": "#ffffff",
      "--c-accent-tint": "rgba(47, 93, 36, 0.08)",
      "--c-text": "#1d2a18",
      "--c-text-muted": "#4c5a44",
      "--c-text-subtle": "#71805f",
      "--c-bg": "#f6f7f2",
      "--c-bg-alt": "#eaeee2",
      "--c-bg-subtle": "#fafbf7",
      "--c-border": "#dbe0cd",
      "--c-border-strong": "#93a181",
      "--font-heading": SERIF,
    },
    dark: {
      "--c-accent": "#93c178",
      "--c-accent-hover": "#a9d190",
      "--c-accent-active": "#7dad61",
      "--c-accent-contrast": "#141c0e",
      "--c-accent-tint": "rgba(147, 193, 120, 0.13)",
      "--c-text": "#e3e9dc",
      "--c-text-muted": "#a8b59d",
      "--c-text-subtle": "#7b8a6e",
      "--c-bg": "#121710",
      "--c-bg-alt": "#1c231a",
      "--c-bg-subtle": "#171d15",
      "--c-border": "#2c3527",
      "--c-border-strong": "#4d5c45",
      "--font-heading": SERIF,
    },
  },
};

/** Apply a palette to the document root by setting CSS custom properties. */
export function applyPalette(theme: FamilyTheme, mode: "light" | "dark"): void {
  const palette = PALETTES[theme];
  if (!palette) return;
  const vars = mode === "dark" ? palette.dark : palette.light;
  const root = document.documentElement;
  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(key, value);
  }
}

/** Remove all palette overrides (reset to CSS defaults). */
export function clearPalette(): void {
  const root = document.documentElement;
  const keys = Object.keys(PALETTES.classic.light);
  for (const key of keys) {
    root.style.removeProperty(key);
  }
}
